/**
 * Financial - adopted model parameters + 25-yr projection, with a Cabinet+
 * interactive editor (CHAP-285; was read-only per CHAP-80).
 *
 * Cabinet/team/admin can toggle "Edit model", drag sliders / type inputs, watch
 * the projection recompute live, then Save (PATCH /api/c/:slug/financial/:id).
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { calculatePMT } from '../../shared/financial.js';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { useUserRoles } from '../hooks/useUserRoles.js';
import { apiPatch } from '../lib/api.js';
import {
  formatCurrency, formatCurrencyFull, formatPercent, formatRatio,
} from '../lib/formatters.js';
import type { FinancialModel } from '../../shared/types.js';
import {
  deriveNoiBase, hasBedsRent, impliedRentPerBedMonthly,
} from '../../shared/financial.js';

interface YearRow {
  year: number;
  noi: number;
  pi: number;
  cashFlow: number;
  propertyValue: number;
}

/** Numeric model inputs - the editable surface + the projection source. */
interface ModelInputs {
  interestRatePct: number;
  termYears: number;
  loanAmount: number;
  noiBase: number;
  noiGrowthRatePct: number;
  capRatePct: number;
  // CHAP-300 - beds x rent-per-bed drivers. 0 means "unset" (legacy manual-NOI
  // path); both positive => base NOI derives as beds * rent * 12.
  beds: number;
  rentPerBedMonthly: number;
}

function parseNum(v: string | null | undefined): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function inputsFromModel(model: FinancialModel): ModelInputs {
  return {
    interestRatePct: parseNum(model.interestRate),
    termYears: model.termYears ?? 25,
    loanAmount: model.loanAmount ?? 0,
    noiBase: model.noi ?? 0,
    noiGrowthRatePct: parseNum(model.noiGrowthRate),
    capRatePct: parseNum(model.capRate) || 6.0,
    beds: model.beds ?? 0,
    rentPerBedMonthly: model.rentPerBedMonthly ?? 0,
  };
}

function buildProjection(inp: ModelInputs): YearRow[] {
  const monthlyPmt = calculatePMT(inp.interestRatePct / 100, inp.termYears, inp.loanAmount);
  const annualPI = Math.round(monthlyPmt * 12);
  const capRate = inp.capRatePct || 6.0;
  // CHAP-300 - base NOI derives from beds x rent x 12 when both are set;
  // otherwise the legacy manual NOI is used unchanged.
  const base = deriveNoiBase({ beds: inp.beds, rentPerBedMonthly: inp.rentPerBedMonthly, noi: inp.noiBase });
  const out: YearRow[] = [];
  for (let i = 0; i <= 25; i++) {
    const noi = base * Math.pow(1 + inp.noiGrowthRatePct / 100, i);
    out.push({
      year: i,
      noi: Math.round(noi),
      pi: annualPI,
      cashFlow: Math.round(noi - annualPI),
      propertyValue: Math.round(noi / (capRate / 100)),
    });
  }
  return out;
}

export default function Financial() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');
  const { allowedRoles, tier } = useUserRoles();
  const canEdit = allowedRoles.includes('team') || allowedRoles.includes('admin') || tier === 'cabinet';
  const qc = useQueryClient();

  const [activeModelId, setActiveModelId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ModelInputs | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const activeModel = useMemo<FinancialModel | null>(() => {
    if (!data || data.financialModels.length === 0) return null;
    if (activeModelId) {
      return data.financialModels.find((m) => m.id === activeModelId) ?? data.financialModels[0]!;
    }
    return data.financialModels.find((m) => m.isPrimary) ?? data.financialModels[0]!;
  }, [data, activeModelId]);

  // Seed/refresh the draft whenever the active model changes.
  useEffect(() => {
    if (activeModel) setDraft(inputsFromModel(activeModel));
    setEditing(false);
  }, [activeModel]);

  const inputs: ModelInputs | null = editing && draft ? draft : activeModel ? inputsFromModel(activeModel) : null;
  const projection = useMemo(() => (inputs ? buildProjection(inputs) : []), [inputs]);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data || !activeModel || !inputs) return <PageState label="No financial model seeded for this tenant." />;

  const yr0 = projection[0];
  const yr10 = projection[10];
  const yr25 = projection[25];
  const yr1 = projection[1] ?? yr0;
  const dscr = yr1 && yr1.pi > 0 ? yr1.noi / yr1.pi : 0;

  // CHAP-300 - when beds + rent are both set, NOI is derived (read-only) and the
  // projection tracks the rent slider live. `inputs` is the draft while editing,
  // so these reflect the in-progress edit.
  const derived = hasBedsRent({ beds: inputs.beds, rentPerBedMonthly: inputs.rentPerBedMonthly, noi: inputs.noiBase });
  const effectiveNoi = deriveNoiBase({ beds: inputs.beds, rentPerBedMonthly: inputs.rentPerBedMonthly, noi: inputs.noiBase });
  // Stable rent-slider bounds from the active model (not the live draft) so the
  // max does not move mid-drag. Falls back to the NOI-implied rent for legacy
  // models that have no stored rent yet.
  const baseRent = activeModel.rentPerBedMonthly ?? impliedRentPerBedMonthly(activeModel.noi ?? 0, activeModel.beds ?? 0);
  const rentMax = Math.max(Math.round(baseRent * 3), 1000);
  const rentStep = Math.max(Math.round(rentMax / 200), 1);

  const setField = (k: keyof ModelInputs) => (v: number) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  async function save() {
    if (!draft || !activeModel) return;
    setSaving(true);
    setSaveErr(null);
    try {
      // CHAP-300 - when beds + rent are both set, persist the derived NOI so the
      // stored `noi` column stays in sync with beds x rent x 12 (other surfaces -
      // ExecutiveSummary, Overview - read the column directly). Otherwise clear
      // the drivers to null and keep the manual NOI.
      const bedsSet = draft.beds > 0 && draft.rentPerBedMonthly > 0;
      const noiToPersist = deriveNoiBase({
        beds: draft.beds, rentPerBedMonthly: draft.rentPerBedMonthly, noi: draft.noiBase,
      });
      await apiPatch(`/api/c/${slug}/financial/${activeModel.id}`, {
        interestRate: draft.interestRatePct,
        termYears: Math.round(draft.termYears),
        capRate: draft.capRatePct,
        loanAmount: Math.round(draft.loanAmount),
        noi: Math.round(noiToPersist),
        noiGrowthRate: draft.noiGrowthRatePct,
        beds: bedsSet ? Math.round(draft.beds) : null,
        rentPerBedMonthly: bedsSet ? Math.round(draft.rentPerBedMonthly) : null,
      });
      await qc.invalidateQueries({ queryKey: ['tenant-config', slug] });
      setEditing(false);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    if (activeModel) setDraft(inputsFromModel(activeModel));
    setEditing(false);
    setSaveErr(null);
  }

  return (
    <div className="space-y-4 sm:space-y-5 min-w-0">
      {/* Hero */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Financial Model</h2>
            <p className="text-neutral text-sm sm:text-base mt-1">
              {data.financialModels.length} model{data.financialModels.length === 1 ? '' : 's'} adopted for {data.tenant.displayName}
            </p>
          </div>
          {yr25 && (
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-xs uppercase tracking-wide text-neutral font-bold">Year-25 property value</span>
              <span className="text-positive text-2xl sm:text-3xl font-bold">{formatCurrency(yr25.propertyValue)}</span>
              <span className="text-xs text-neutral">at {formatPercent(inputs.capRatePct)} cap rate</span>
            </div>
          )}
        </div>
      </div>

      {/* Scenario picker - only show if >1 model */}
      {data.financialModels.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <p className="text-xs uppercase tracking-wider text-neutral font-semibold mb-2">Scenario</p>
          <div className="flex flex-wrap gap-2">
            {data.financialModels.map((m) => {
              const isActive = m.id === activeModel.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setActiveModelId(m.id)}
                  className={`text-xs sm:text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                    isActive
                      ? 'bg-navy text-white border-navy font-semibold'
                      : 'bg-white text-navy border-gray-200 hover:border-navy/40'
                  }`}
                >
                  {m.name}
                  {m.isPrimary && (
                    <span className={`ml-1.5 text-[10px] px-1 rounded ${isActive ? 'bg-white/20 text-white' : 'bg-orange/10 text-orange'}`}>
                      Primary
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Model parameters / editor card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2 min-w-0">
          <h3 className="text-navy font-semibold text-sm sm:text-base">
            {activeModel.name} - Model Inputs {editing && <span className="text-orange text-xs">(editing)</span>}
          </h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            {activeModel.storyCount != null && !editing && (
              <span className="text-xs uppercase tracking-wider bg-gray-100 text-navy px-1.5 py-0.5 rounded font-semibold">
                {activeModel.storyCount} stories
              </span>
            )}
            {canEdit && !editing && (
              <button type="button" onClick={() => setEditing(true)} className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 font-medium">
                Edit model
              </button>
            )}
            {canEdit && editing && (
              <>
                <button type="button" onClick={cancel} disabled={saving} className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <button type="button" onClick={save} disabled={saving} className="text-xs px-3 py-1.5 rounded bg-navy text-white font-medium disabled:opacity-50">{saving ? 'Saving…' : 'Save model'}</button>
              </>
            )}
          </div>
        </div>

        {editing && draft ? (
          <div className="grid sm:grid-cols-2 gap-4 min-w-0">
            <NumberField label="Beds" value={draft.beds} onChange={setField('beds')} display={draft.beds > 0 ? String(draft.beds) : 'Not set'} min={0} step={1} />
            <Slider label="Rent per bed / mo" suffix="$" min={0} max={rentMax} step={rentStep} value={draft.rentPerBedMonthly} onChange={setField('rentPerBedMonthly')} display={draft.rentPerBedMonthly > 0 ? formatCurrency(draft.rentPerBedMonthly) : 'Not set'} />
            <Slider label="Interest rate" suffix="%" min={0} max={15} step={0.05} value={draft.interestRatePct} onChange={setField('interestRatePct')} display={formatPercent(draft.interestRatePct, 2)} />
            <Slider label="Term" suffix="yr" min={5} max={40} step={1} value={draft.termYears} onChange={setField('termYears')} display={`${Math.round(draft.termYears)} yr`} />
            <Slider label="NOI growth" suffix="%" min={-5} max={10} step={0.1} value={draft.noiGrowthRatePct} onChange={setField('noiGrowthRatePct')} display={formatPercent(draft.noiGrowthRatePct, 1)} />
            <Slider label="Cap rate" suffix="%" min={3} max={12} step={0.1} value={draft.capRatePct} onChange={setField('capRatePct')} display={formatPercent(draft.capRatePct, 1)} />
            <NumberField label="Loan amount" value={draft.loanAmount} onChange={setField('loanAmount')} display={formatCurrency(draft.loanAmount)} />
            {derived ? (
              <DerivedStat label="NOI (Yr 1)" value={formatCurrency(effectiveNoi)} hint={`${draft.beds} beds x ${formatCurrency(draft.rentPerBedMonthly)}/mo x 12`} />
            ) : (
              <NumberField label="NOI (Yr 1)" value={draft.noiBase} onChange={setField('noiBase')} display={formatCurrency(draft.noiBase)} />
            )}
            {saveErr && <p className="sm:col-span-2 text-xs text-red-600">{saveErr}</p>}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <Param label="Loan amount"     value={formatCurrency(inputs.loanAmount)} />
            <Param label="Interest rate"   value={formatPercent(inputs.interestRatePct, 2)} />
            <Param label="Term"            value={`${inputs.termYears} yr`} />
            <Param label="NOI (Yr 1)"      value={formatCurrency(effectiveNoi)} positive />
            {derived && <Param label="Beds"            value={String(inputs.beds)} />}
            {derived && <Param label="Rent / bed / mo" value={formatCurrency(inputs.rentPerBedMonthly)} />}
            <Param label="NOI growth"      value={formatPercent(inputs.noiGrowthRatePct, 1)} />
            <Param label="Cap rate"        value={formatPercent(inputs.capRatePct, 1)} />
            {yr1 && <Param label="Annual P&I"     value={formatCurrency(yr1.pi)} />}
            {yr1 && <Param label="DSCR (Yr 1)"    value={formatRatio(dscr, 2)} positive={dscr >= 1.2} />}
            {yr1 && <Param label="Cash flow Yr 1" value={formatCurrency(yr1.cashFlow)} positive={yr1.cashFlow > 0} />}
          </div>
        )}
      </div>

      {/* 25-Year Projection chart */}
      {projection.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2 min-w-0">
            <h3 className="text-navy font-semibold text-sm sm:text-base">25-Year NOI + Cash Flow Projection</h3>
            <span className="text-xs text-neutral">Compound growth {formatPercent(inputs.noiGrowthRatePct, 1)}/yr</span>
          </div>
          <div className="h-64 sm:h-72 lg:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={projection} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#94a3b8' }} label={{ value: 'Year', position: 'insideBottom', offset: -5, fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)}M`} width={50} />
                <RechartsTooltip
                  formatter={(v) => formatCurrencyFull(v as number)}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Line type="monotone" dataKey="noi"           name="NOI"             stroke="#22C55E" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="pi"            name="Annual P&I"      stroke="#F26522" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="cashFlow"      name="Cash Flow"       stroke="#3B82F6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="propertyValue" name="Property Value"  stroke="#8B5CF6" strokeWidth={2} dot={false} strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Year milestones */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <YearTile label="Year 1"  row={yr1 ?? yr0!} />
        {yr10 && <YearTile label="Year 10" row={yr10} />}
        {yr25 && <YearTile label="Year 25 (mortgage paid)" row={yr25} highlight />}
      </div>

      {!canEdit && (
        <div className="bg-navy/5 rounded-lg p-3 text-xs text-neutral">
          <span className="font-medium text-navy">Read-only.</span> Cabinet members can edit the model inputs.
        </div>
      )}
    </div>
  );
}

function Slider({ label, suffix, min, max, step, value, onChange, display }: {
  label: string; suffix: string; min: number; max: number; step: number; value: number; onChange: (v: number) => void; display: string;
}) {
  return (
    <label className="block min-w-0">
      <div className="flex items-baseline justify-between">
        <span className="text-neutral text-xs uppercase tracking-wider">{label}</span>
        <span className="text-navy font-bold text-sm">{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full mt-1 accent-navy" aria-label={`${label} (${suffix})`} />
    </label>
  );
}

function NumberField({ label, value, onChange, display, min = 0, step = 1000 }: {
  label: string; value: number; onChange: (v: number) => void; display: string; min?: number; step?: number;
}) {
  return (
    <label className="block min-w-0">
      <div className="flex items-baseline justify-between">
        <span className="text-neutral text-xs uppercase tracking-wider">{label}</span>
        <span className="text-navy font-bold text-sm">{display}</span>
      </div>
      <input type="number" min={min} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full mt-1 border border-gray-300 rounded px-2 py-1 text-sm" />
    </label>
  );
}

/** Read-only NOI display when it is derived from beds x rent (CHAP-300). */
function DerivedStat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="block min-w-0">
      <div className="flex items-baseline justify-between">
        <span className="text-neutral text-xs uppercase tracking-wider">{label}</span>
        <span className="text-navy font-bold text-sm">{value}</span>
      </div>
      <p className="mt-1 text-[11px] text-neutral truncate">Derived: {hint}</p>
    </div>
  );
}

function Param({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-neutral text-xs uppercase tracking-wider">{label}</p>
      <p className={`font-bold text-sm sm:text-base truncate ${positive ? 'text-positive' : 'text-navy'}`}>{value}</p>
    </div>
  );
}

function YearTile({ label, row, highlight }: { label: string; row: YearRow; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 sm:p-4 min-w-0 ${highlight ? 'border-orange/40 bg-orange/5' : 'border-gray-100 bg-white shadow-sm'}`}>
      <p className={`text-xs uppercase tracking-wider font-semibold ${highlight ? 'text-orange' : 'text-neutral'}`}>{label}</p>
      <div className="mt-2 space-y-1.5 text-sm">
        <Row label="NOI"            value={formatCurrency(row.noi)} positive />
        <Row label="P&I"            value={formatCurrency(row.pi)} />
        <Row label="Cash flow"      value={formatCurrency(row.cashFlow)} positive={row.cashFlow > 0} />
        <Row label="Property value" value={formatCurrency(row.propertyValue)} />
      </div>
    </div>
  );
}

function Row({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2 min-w-0">
      <span className="text-xs text-neutral truncate">{label}</span>
      <span className={`text-xs sm:text-sm font-bold ${positive ? 'text-positive' : 'text-navy'} flex-shrink-0`}>{value}</span>
    </div>
  );
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
